/**
 * useNotifications — data layer for the Pro bell + panel.
 *
 * Calls Plan A's JSON HTTP surface exclusively:
 *   GET  /api/professional/notifications          (list, newest-first, paged)
 *   GET  /api/professional/notifications/unread-count
 *   POST /api/professional/notifications/:id/read (marks one row read)
 *
 * The FE owns no notification truth — mutations call through to the server.
 *
 * StrictMode-safe: the prefetch dedupes on `loaded` not `started`.
 * StrictMode's double-mount effect fires: mount → start async → unmount →
 * remount → the second mount sees loaded=true and skips the fetch. This means
 * the second mount doesn't race a fresh load against the first. The pattern
 * is identical to the "dedupe on loaded" lesson documented in memory.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  getNotificationsUnreadCount,
  listNotifications,
  markNotificationRead,
  type NotificationItem,
} from "../lib/api";
import { FIXTURE_NOTIFICATIONS } from "../lib/fixtures";
import { IS_PREVIEW } from "../lib/pro-mode";
import { useProToken } from "./useProToken";

// ─── State shape ─────────────────────────────────────────────────────────────

type State =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "loaded";
      items: NotificationItem[];
      nextCursor: string | undefined;
      hasMore: boolean;
      unreadCount: number;
    }
  | { status: "error"; error: string };

type Action =
  | { type: "LOAD_START" }
  | {
      type: "LOAD_DONE";
      items: NotificationItem[];
      nextCursor: string | undefined;
      unreadCount: number;
    }
  | { type: "LOAD_ERROR"; error: string }
  | {
      type: "LOAD_MORE_DONE";
      items: NotificationItem[];
      nextCursor: string | undefined;
    }
  | { type: "UNREAD_COUNT"; count: number }
  | { type: "MARK_READ"; id: string };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "LOAD_START":
      return { status: "loading" };

    case "LOAD_DONE":
      return {
        status: "loaded",
        items: action.items,
        nextCursor: action.nextCursor,
        hasMore: !!action.nextCursor,
        unreadCount: action.unreadCount,
      };

    case "LOAD_ERROR":
      return { status: "error", error: action.error };

    case "LOAD_MORE_DONE":
      if (state.status !== "loaded") return state;
      return {
        ...state,
        items: [...state.items, ...action.items],
        nextCursor: action.nextCursor,
        hasMore: !!action.nextCursor,
      };

    case "UNREAD_COUNT":
      if (state.status !== "loaded") return state;
      return { ...state, unreadCount: action.count };

    case "MARK_READ": {
      if (state.status !== "loaded") return state;
      const item = state.items.find((candidate) => candidate.id === action.id);
      if (item?.read_state === "read") return state;
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.id
            ? { ...item, read_state: "read" as const }
            : item,
        ),
        // The server count and row dot use the same truth: anything not read.
        // A subscriber may not have the row in its current page, but it still
        // needs to update the shared bell count immediately.
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }

    default:
      return state;
  }
}

// ─── Poll interval ────────────────────────────────────────────────────────────

/** Modest interval poll for v1 (no realtime channel yet). */
const POLL_INTERVAL_MS = 45_000;

/** Unread count over the preview fixtures (IS_PREVIEW paths only). */
const fixtureUnreadCount = () =>
  FIXTURE_NOTIFICATIONS.filter((n) => n.read_state !== "read").length;

// Every mounted notification surface (header bell, panel, full log) owns a
// hook instance. Broadcast explicit read transitions so their dots and badge
// counts stay synchronized without waiting for the next poll.
const readSubscribers = new Set<(id: string) => void>();

function broadcastRead(id: string) {
  for (const subscriber of readSubscribers) subscriber(id);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export type NotificationsStatus = "idle" | "loading" | "loaded" | "error";

export interface UseNotificationsResult {
  status: NotificationsStatus;
  items: NotificationItem[];
  unreadCount: number;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

export function useNotifications(): UseNotificationsResult {
  const [state, dispatch] = useReducer(reducer, { status: "idle" });
  const getToken = useProToken();

  // StrictMode-safe dedupe: track "loaded" not "started".
  const loadedRef = useRef(false);

  // Stable ref for getToken so the effect closure doesn't stale.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  });

  useEffect(() => {
    const subscriber = (id: string) => dispatch({ type: "MARK_READ", id });
    readSubscribers.add(subscriber);
    return () => {
      readSubscribers.delete(subscriber);
    };
  }, []);

  const load = useCallback(async () => {
    // Preview (IS_PREVIEW): no Clerk/backend — serve fixtures so the bell,
    // panel, and /notifications log render with sample enriched rows (SILL-414).
    if (IS_PREVIEW) {
      dispatch({
        type: "LOAD_DONE",
        items: FIXTURE_NOTIFICATIONS,
        nextCursor: undefined,
        unreadCount: fixtureUnreadCount(),
      });
      return;
    }
    const token = await getTokenRef.current();
    dispatch({ type: "LOAD_START" });
    try {
      const [listRes, unreadCount] = await Promise.all([
        listNotifications({}, token ?? undefined),
        getNotificationsUnreadCount(token ?? undefined),
      ]);
      dispatch({
        type: "LOAD_DONE",
        items: listRes.notifications,
        nextCursor: listRes.next_cursor,
        unreadCount,
      });
    } catch (err) {
      dispatch({
        type: "LOAD_ERROR",
        error:
          err instanceof Error ? err.message : "Failed to load notifications",
      });
    }
  }, []);

  // Initial load + polling — deduped on loadedRef so StrictMode double-mount
  // does not trigger two parallel fetches.
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();

    const interval = setInterval(() => {
      void load();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      // Reset so a genuine remount (route change) reloads.
      loadedRef.current = false;
    };
  }, [load]);

  const loadMore = useCallback(async () => {
    if (state.status !== "loaded" || !state.hasMore) return;
    const token = await getTokenRef.current();
    try {
      const res = await listNotifications(
        { cursor: state.nextCursor },
        token ?? undefined,
      );
      dispatch({
        type: "LOAD_MORE_DONE",
        items: res.notifications,
        nextCursor: res.next_cursor,
      });
    } catch {
      // Silently ignore load-more errors — the existing rows stay visible.
    }
  }, [state]);

  const markRead = useCallback(async (id: string) => {
    // Optimistically update every mounted notification surface.
    broadcastRead(id);
    if (IS_PREVIEW) return; // no backend in preview — keep the optimistic flip only
    try {
      const token = await getTokenRef.current();
      await markNotificationRead(id, token ?? undefined);
    } catch {
      // Best-effort.
    }
  }, []);

  const reload = useCallback(async () => {
    loadedRef.current = false;
    await load();
    loadedRef.current = true;
  }, [load]);

  if (state.status === "loaded") {
    return {
      status: "loaded",
      items: state.items,
      unreadCount: state.unreadCount,
      hasMore: state.hasMore,
      loadMore,
      markRead,
      reload,
    };
  }

  return {
    status: state.status,
    items: [],
    unreadCount: 0,
    hasMore: false,
    loadMore,
    markRead,
    reload,
  };
}

// ─── Unread count only (for bell badge without full panel state) ───────────────

/**
 * Lightweight unread-count polling hook used by the Bell when the panel is
 * not yet open. Avoids fetching the full list until the panel is opened.
 *
 * Same StrictMode-safe dedupe on "loaded" pattern.
 */
export function useNotificationUnreadCount(): number {
  // In preview, seed the count from fixtures via the lazy initializer so the
  // effect never calls setState synchronously (no cascading-render lint hit).
  const [count, setCount] = useState(() =>
    IS_PREVIEW ? fixtureUnreadCount() : 0,
  );
  const getToken = useProToken();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    if (IS_PREVIEW) return; // count seeded from fixtures at init; no polling

    const fetchCount = async () => {
      try {
        const token = await getToken();
        const n = await getNotificationsUnreadCount(token ?? undefined);
        setCount(n);
      } catch {
        // Silently ignore — badge stays at 0 on error.
      }
    };

    void fetchCount();
    const interval = setInterval(() => void fetchCount(), POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      loadedRef.current = false;
    };
  }, [getToken]);

  return count;
}
